import { flowRight, debounce } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '../../../../common/components/runtime-context';
import classNames from 'classnames';
import { APP_TEXT_COLOR_PATH } from '../../../constants/wix-params';
import { ModalCentered } from '../base';
import { getCaptchaSvg, getCaptchaId } from '../../../selectors/captcha-selectors';
import ButtonGroup from '../../button-group';
import { ReplaceIcon, LoadingIcon, ErrorIcon, CheckMarkIconV2 } from '../../icons';
import withFontClassName from '../../../hoc/with-font-class-name';
import withTranslate from '../../../hoc/with-translate';
import withSettingsColor from '../../../hoc/with-settings-color';
import styles from './captcha-confirmation-modal-component.scss';

class CaptchaConfirmationModal extends React.Component {
  state = {
    answer: '',
    isCorrectAnswer: false,
    isInputFocused: false,
    isValidating: false,
    hasTriggeredValidation: false,
    isReloadingCaptcha: false,
  };

  onAnswerInput = ev => {
    const val = ev.target.value;
    this.setState({ answer: val });

    this.validateAnswer(val);
  };

  validateAnswer = debounce(() => {
    const value = this.state.answer;

    if (this.validationPromise || value.length < 4) {
      return;
    }

    this.setState({ isValidating: true, hasTriggeredValidation: true });
    this.validationPromise = this.props
      .validateCaptchaPromisified({
        id: this.props.captchaId,
        answer: value,
      })
      .then(response => {
        this.setState({ isCorrectAnswer: response.ok, isValidating: false });
        this.validationPromise = null;
        if (this.state.answer !== value) {
          this.validateAnswer();
        }
      });
  }, 100);

  reload = () => {
    this.props.fetchCaptchaPromisified().then(() => this.setState({ isReloadingCaptcha: false }));
    this.setState({
      answer: '',
      isCorrectAnswer: false,
      hasTriggeredValidation: false,
      isReloadingCaptcha: true,
    });
  };

  handleKeyPress = e => {
    if (e.keyCode === 13 && this.state.isCorrectAnswer) {
      // enter
      this.props.confirm();
    }
  };

  getBorderStyle = () => {
    const { borderColorFocused, borderColorNotFocused } = this.props;
    const { hasTriggeredValidation, isValidating, isCorrectAnswer, isInputFocused } = this.state;

    let color;

    if (!isValidating && hasTriggeredValidation && !isCorrectAnswer) {
      color = '#e84a43';
    } else if (isInputFocused) {
      color = borderColorFocused;
    } else {
      color = borderColorNotFocused;
    }

    return { border: `1px solid ${color}` };
  };

  render() {
    const {
      captchaSvg,
      closeModal,
      confirm,
      primaryActionTextKey,
      contentFontClassName,
      textColor,
      borderColorNotFocused,
      t,
    } = this.props;

    const {
      hasTriggeredValidation,
      isValidating,
      isCorrectAnswer,
      isReloadingCaptcha,
    } = this.state;

    return (
      <ModalCentered onClose={closeModal}>
        <div
          style={{ color: textColor }}
          className={classNames(styles.container, contentFontClassName)}
        >
          <p className={styles.infoText}>{t('captcha.main-text')}</p>
          <div className={styles.captchaContainer}>
            <div
              className={styles.captcha}
              style={{ border: `1px solid ${borderColorNotFocused}` }}
              dangerouslySetInnerHTML={{ __html: captchaSvg }}
            />
            <ReplaceIcon
              className={classNames(styles.reloadButton, 'button-icon-fill', {
                [styles.rotateOnce]: isReloadingCaptcha,
              })}
              onClick={this.reload}
            />
          </div>

          <div className={styles.inputWrapper}>
            <input
              className={styles.input}
              value={this.state.answer}
              onChange={this.onAnswerInput}
              onKeyDown={this.handleKeyPress}
              onFocus={() => this.setState({ isInputFocused: true })}
              onBlur={() => this.setState({ isInputFocused: false })}
              style={this.getBorderStyle()}
              placeholder={t('captcha.input-placeholder')}
              type="text"
            />
            <div className={styles.inputIconWrapper}>
              {isValidating && (
                <LoadingIcon className={classNames(styles.rotateAnimation, 'button-icon-fill')} />
              )}
              {!isValidating && isCorrectAnswer && (
                <CheckMarkIconV2 className={classNames('button-icon-fill')} />
              )}
              {!isValidating && hasTriggeredValidation && !isCorrectAnswer && (
                <ErrorIcon className={classNames(styles.failIcon)} />
              )}
            </div>
          </div>
        </div>

        <ButtonGroup
          primaryButtonText={t(primaryActionTextKey)}
          primaryButtonProps={{
            onClick: () => confirm(),
            disabled: !isCorrectAnswer,
          }}
          secondaryButtonText={t('captcha.cancel')}
          secondaryButtonProps={{ onClick: () => closeModal() }}
          isCentered
        />
      </ModalCentered>
    );
  }
}

CaptchaConfirmationModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  confirm: PropTypes.func.isRequired,
  primaryActionTextKey: PropTypes.string.isRequired,
  validateCaptchaPromisified: PropTypes.func.isRequired,
  fetchCaptchaPromisified: PropTypes.func.isRequired,
  captchaSvg: PropTypes.string.isRequired,
  captchaId: PropTypes.string.isRequired,
  textColor: PropTypes.string.isRequired,
  contentFontClassName: PropTypes.string.isRequired,
  borderColorNotFocused: PropTypes.string.isRequired,
  borderColorFocused: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

const mapRuntimeToProps = (state, ownProps, actions) => ({
  captchaSvg: getCaptchaSvg(state),
  captchaId: getCaptchaId(state),
  confirm: () => ownProps.closeModal({ isSuccessful: true }),
  fetchCaptchaPromisified: actions.fetchCaptchaPromisified,
  validateCaptchaPromisified: actions.validateCaptchaPromisified,
});

export default flowRight(
  connect(mapRuntimeToProps),
  withFontClassName,
  withSettingsColor({
    propName: 'textColor',
    siteColorFallback: 'color-5',
    fallbackColor: '#000000',
  }),
  withSettingsColor({
    path: APP_TEXT_COLOR_PATH,
    propName: 'borderColorFocused',
    alpha: 0.4,
    siteColorFallback: 'color-5',
    siteColorAlpha: 0.4,
    fallbackColor: '#000000',
  }),
  withSettingsColor({
    path: APP_TEXT_COLOR_PATH,
    propName: 'borderColorNotFocused',
    alpha: 0.2,
    siteColorFallback: 'color-5',
    siteColorAlpha: 0.2,
    fallbackColor: '#000000',
  }),
  withTranslate,
)(CaptchaConfirmationModal);
